// Admin → Accounts tab: subscription status card + quota usage.
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { ChartCard, EmptyState } from './shared';

const UsageRow = ({ label, used, limit, unit }) => {
  const u = Number(used) || 0;
  const l = Number(limit);
  const hasLimit = Number.isFinite(l) && l > 0;
  const pct = hasLimit ? Math.min(100, Math.round((u / l) * 100)) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-blue-500';
  return (
    <div className="py-3">
      <div className="flex justify-between text-sm mb-1.5">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-500">{hasLimit ? `${u} / ${l}${unit ? ` ${unit}` : ''}` : `${u}${unit ? ` ${unit}` : ''}`}</span>
      </div>
      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${hasLimit ? pct : 0}%` }} />
      </div>
      {!hasLimit && <p className="text-xs text-slate-400 mt-1">—</p>}
    </div>
  );
};

UsageRow.propTypes = {
  label: PropTypes.string.isRequired,
  used: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string
};

const AdminAccounts = ({ view, t }) => {
  const { metrics, companyInfo = {}, quota } = view;
  const sub = companyInfo.subscription || {};
  const days = metrics.subscriptionDaysRemaining ?? sub.daysRemaining ?? null;
  const urgent = days != null && days <= 7;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartCard title={t('subTitle')} subtitle={companyInfo.name || ''}>
        <div className={`p-4 rounded-lg border ${urgent ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <p className="text-sm font-semibold text-slate-800">
            {t('subStatus')}: {sub.status || t('unknown')}
          </p>
          <p className={`text-2xl font-bold mt-1 ${urgent ? 'text-red-600' : 'text-green-700'}`}>
            {days != null ? t('daysLeft', { count: days }) : t('noSubInfo')}
          </p>
          {companyInfo.domain && <p className="text-xs text-slate-500 mt-1">{companyInfo.domain}</p>}
        </div>
        <Link to="/admin/subscription"
          className="mt-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800">
          {t('manageSubscription')}
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </ChartCard>

      <ChartCard title={t('usageTitle')} subtitle={t('usageSubtitle')}>
        {quota || companyInfo.userLimit != null || companyInfo.maxUsers != null ? (
          <div className="divide-y divide-slate-100">
            <UsageRow label={t('quotaUsers')}
              used={quota?.usersUsed ?? metrics.totalUsers}
              limit={quota?.usersLimit ?? companyInfo.userLimit ?? companyInfo.maxUsers} />
            <UsageRow label={t('quotaAnalyses')}
              used={quota?.analysesUsed ?? metrics.totalAnalyses ?? metrics.activeAnalyses}
              limit={quota?.analysesLimit ?? companyInfo.analysisLimit ?? companyInfo.maxAnalyses} />
            {(quota?.storageLimit != null || companyInfo.storageLimit != null) && (
              <UsageRow label={t('quotaStorage')}
                used={quota?.storageUsed ?? companyInfo.storageUsed}
                limit={quota?.storageLimit ?? companyInfo.storageLimit} unit="GB" />
            )}
          </div>
        ) : (
          <EmptyState message={t('noQuota')} />
        )}
      </ChartCard>
    </div>
  );
};

AdminAccounts.propTypes = {
  view: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired
};

export default AdminAccounts;
